// Pure decision logic for deleting a scheduled message from the delete button.
//
// #149: deleting a recurring message from the calendar sidebar destroyed the
// WHOLE series. delete-message-button.tsx suppressed the scope options when
// `openedFromContext === 'calendar-sidebar'` and then hard-coded the delete
// scope to "All messages", discarding the user's selection — so what looked
// like deleting one occurrence removed every future one, unrecoverably.
//
// The fix moves the decision here, where it takes NO surface/context argument:
// context is structurally unable to influence the recurring scope. Kept pure so
// it is unit-testable in the repo's node (no-jsdom) jest environment.

export interface DeletableMessage {
  isCampaign?: boolean;
  isRecurring?: boolean;
  rruleset?: unknown;
}

/** The whole-series delete option — the only genuinely destructive scope. */
export const ALL_MESSAGES_SCOPE = 'All messages';
/** The safe, least-destructive scope (removes a single occurrence via patch). */
export const THIS_MESSAGE_SCOPE = 'This message';

export type DeleteDecision =
  | { kind: 'campaign' }
  | { kind: 'single' }
  | { kind: 'recurring'; scope: string };

/**
 * Whether the delete dialog must offer a scope choice (This / This+following /
 * All). True for every recurring message that carries an rruleset, on EVERY
 * surface — the calendar sidebar used to suppress this and is the reason a
 * single-occurrence delete became a whole-series delete.
 */
export function shouldOfferRecurringScope(message: DeletableMessage | null | undefined): boolean {
  return Boolean(message?.isRecurring && message?.rruleset);
}

/**
 * Index the delete dialog should default to: the least-destructive scope
 * ("This message"), wherever it sits in the option list. This guarantees an
 * accidental confirm removes a single occurrence, never the whole series, even
 * if the option ordering is later changed. Falls back to 0 if absent.
 */
export function safeDefaultScopeIndex(options: readonly string[]): number {
  const i = options.indexOf(THIS_MESSAGE_SCOPE);
  return i >= 0 ? i : 0;
}

/**
 * Decide what a confirmed delete does, from the message and the user's dialog
 * selection ALONE. Deliberately takes no surface/context argument: for a
 * recurring message the scope is exactly what the user selected, and nothing
 * (least of all the surface it was opened from) may widen it (#149).
 */
export function decideDelete(
  message: DeletableMessage | null | undefined,
  userSelection: string | undefined,
): DeleteDecision {
  if (message?.isCampaign) return { kind: 'campaign' };
  if (shouldOfferRecurringScope(message)) {
    return { kind: 'recurring', scope: userSelection as string };
  }
  return { kind: 'single' };
}

/**
 * Honest confirmation title. The old copy said singular "Delete message?" even
 * when the whole recurring series was about to go; name the series explicitly.
 */
export function deleteDialogTitle(message: DeletableMessage | null | undefined): string {
  if (message?.isCampaign) return 'Delete campaign?';
  if (shouldOfferRecurringScope(message)) return 'Delete recurring message?';
  return 'Delete message?';
}
